import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { stats } from '@/mocks/home';

export default function AboutPage() {
  const location = useLocation();
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }
    const formData = new FormData(form);
    try {
      const res = await fetch('https://readdy.ai/api/form/d9rikh4nlsngrm4lmco0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed;
      try { parsed = JSON.parse(text); } catch { parsed = {}; }
      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后再试';
        if (typeof msg === 'string' && (msg.includes('spam') || msg.includes('form data is spam'))) {
          setFormError('提交失败，请稍后再试');
        } else {
          setFormError(msg);
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后再试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于mogu视频',
            description: '了解mogu视频的品牌故事、核心价值和联系方式',
            about: {
              '@type': 'Organization',
              name: 'mogu视频',
              description: '蘑菇街旗下专注时尚购物的短视频平台',
              foundingDate: '2016',
            },
          }),
        }}
      />

      <Navbar />

      <main className="pt-20">
        <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '关于我们' },
            ]}
          />

          <section className="py-16 md:py-20">
            <div className="max-w-[800px] mx-auto text-center mb-14">
              <h1 className="text-3xl md:text-4xl font-bold font-heading text-foreground-950 mb-4">
                关于mogu视频
              </h1>
              <p className="text-base text-foreground-600 leading-relaxed">
                mogu视频是蘑菇街旗下的时尚购物短视频平台，致力于通过视频内容连接时尚与消费，让每一位用户都能在这里发现自己的时尚灵感，享受边看边买的沉浸式购物体验。
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center mb-16 md:mb-20">
              <div>
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-4">
                  品牌故事
                </h2>
                <div className="space-y-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                  <p>
                    mogu视频诞生于2016年，是蘑菇街在短视频电商领域的重要布局。我们洞察到年轻消费者对时尚内容的需求正在从图文向视频迁移，因此打造了一个以视频为核心、融合购物体验的时尚平台。
                  </p>
                  <p>
                    在这里，你可以看到真实的穿搭上身效果、详细的美妆使用教程、客观的单品开箱测评。每一位入驻的时尚达人都经过严格筛选，他们分享的不是广告，而是真实的体验和心得。
                  </p>
                  <p>
                    我们的使命是让每一个热爱时尚的人都能轻松发现适合自己的风格，在观看视频的过程中获得灵感，并通过便捷的购物功能将灵感变为现实。
                  </p>
                </div>
              </div>
              <div className="rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20fashion%20office%20with%20creative%20team%20collaborating%20around%20a%20table%20with%20clothing%20samples%20and%20mood%20boards%2C%20bright%20airy%20loft%20space%20with%20large%20windows%2C%20warm%20natural%20lighting%2C%20creative%20workspace%20aesthetic%2C%20clean%20and%20inspiring%20atmosphere&width=800&height=600&seq=mogu-about-brand&orientation=landscape"
                  alt="mogu视频品牌故事"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-5 md:gap-6 mb-16 md:mb-20">
              {stats.map((stat) => (
                <div
                  key={stat.id}
                  className="bg-background-50 rounded-xl p-6 text-center border border-background-200/70"
                >
                  <div className="text-2xl md:text-3xl font-bold font-heading text-accent-500 mb-1">
                    {stat.value}
                  </div>
                  <div className="text-xs md:text-sm text-foreground-500 whitespace-nowrap">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>

            <div className="mb-16 md:mb-20">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-8 text-center">
                品牌声誉
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                <div className="bg-background-50 rounded-xl p-6 border border-background-200/70 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                    <i className="ri-award-line text-2xl"></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-1">行业认可</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    荣获"最具创新力电商平台"奖项，被多家权威媒体评为"视频购物赛道领跑者"
                  </p>
                </div>
                <div className="bg-background-50 rounded-xl p-6 border border-background-200/70 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                    <i className="ri-shield-check-line text-2xl"></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-1">品质保障</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    蘑菇街供应链体系背书，严格正品审核机制，每一件商品都可追溯来源
                  </p>
                </div>
                <div className="bg-background-50 rounded-xl p-6 border border-background-200/70 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                    <i className="ri-heart-line text-2xl"></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-1">用户信赖</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    超5000万月活用户的选择，98%的用户好评率，千万用户的时尚好伙伴
                  </p>
                </div>
              </div>
            </div>

            <div className="max-w-[600px] mx-auto mb-16">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-8 text-center">
                联系我们
              </h2>

              <div className="bg-background-50 rounded-xl p-6 md:p-8 border border-background-200/70 mb-8">
                <div className="space-y-4 mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-map-pin-line text-lg"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">公司地址</div>
                      <div className="text-sm text-foreground-500">浙江省杭州市西湖区古墩路99号浙商财富中心</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">电子邮箱</div>
                      <div className="text-sm text-foreground-500">contact@mogu-video.com</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-phone-line text-lg"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">客服电话</div>
                      <div className="text-sm text-foreground-500">400-888-XXXX</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-background-50 rounded-xl p-6 md:p-8 border border-background-200/70">
                <h3 className="text-lg font-semibold text-foreground-900 mb-4">给我们留言</h3>
                <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form="">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="about-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        姓名
                      </label>
                      <input
                        id="about-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-4 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-800 outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱
                      </label>
                      <input
                        id="about-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-4 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-800 outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="about-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      留言内容
                    </label>
                    <textarea
                      id="about-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-800 outline-none focus:border-primary-400 transition-colors resize-none"
                      placeholder="请输入您的留言内容（500字以内）"
                    ></textarea>
                  </div>
                  <input
                    name="phone_alt"
                    type="text"
                    className="honeypot-field"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                  <button
                    type="submit"
                    className="w-full px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full text-sm font-semibold transition-all whitespace-nowrap"
                  >
                    提交留言
                  </button>

                  {formStatus === 'success' && (
                    <p className="text-sm text-center text-green-600">留言已提交，我们会尽快回复您！</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-center text-red-500">{formError}</p>
                  )}
                </form>
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer />
      <ScrollToTop />
    </>
  );
}